import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import i18next from 'i18next';

import { LiveHelper } from '../../../util/live-helper';
import { getEmbedShareSettings, joinClassNames, STORY_ACCENT_COLOR } from '../../../util/util';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { isReelsFeedEnabled } from '../../StoryFeed/story-feed-helper';
import { usePlayer } from '../../../contexts/PlayerContext';

import {
    TopControlsAccountTitle,
    TopControlsContainer,
    TopControlsMediaTitle,
    TopControlsPlaybackTitle,
    TopControlsPlaybackTitleWrapper,
    TopControlsProps
} from './TopControls.styled';

export const StoryTopControl = ({ className, settings, isMobile }: TopControlsProps) => {
    const { t } = useTranslation();
    const { currentPosition, duration: durationReducer, isPlaying, setPlayerCurrentPosition } = usePlayer();
    const { mediaName, embedShareSettings: embedShareSettingsMessage, configParams, isMediaLive, mediasData, currentMediaId } = useConfiguration();

    const timelineBarRef = useRef<HTMLDivElement | null>(null);
    const [indicatorPosition, setIndicatorPosition] = useState(0);
    const [overShowingTime, setOverShowingTime] = useState<boolean>(false);

    const embedShareSettings = getEmbedShareSettings({ configParams, messageData: embedShareSettingsMessage });
    const currentPlayerId = useMemo(() => `player-${currentMediaId}-${configParams.containerKey}`, [currentMediaId, configParams.containerKey]);
    const solidColor = settings?.controls?.solidBackgroundTopColor ?? '#00000';
    const solidColorStyle = settings?.controls.showSolidBackgroundTopControl
        ? {
            backgroundColor: tinycolor({
                ...tinycolor(solidColor).toRgb(),
                a: Number(settings?.controls.solidBackgroundTopControlOpacity ?? 100) / 100,
            }).toRgbString(),
        }
        : undefined;
    const { primaryColor } = useMemo(() => {
        const playerColor = settings?.playerStyling?.primaryColor ?? STORY_ACCENT_COLOR;
        return {
            color: playerColor,
            primaryColor: { backgroundColor: playerColor },
        };
    }, [settings?.playerStyling?.primaryColor]);
    const progressTranslate = useMemo(
        () => ({
            width: `${indicatorPosition}%`,
        }),
        [indicatorPosition]
    );
    const currentMedia = useMemo(
        () => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
        [mediasData, currentMediaId]
    );
    const accountName = useMemo(() => {
        if (!settings?.controls?.accountName) {
            return '';
        }
        if (typeof settings?.controls?.accountName === 'string') {
            return settings?.controls?.accountName;
        }

        const currentLanguage = i18next.languages[0] === 'no' ? 'nb' : i18next.languages[0] === 'dk' ? 'da' : i18next.languages[0];
        return settings?.controls?.accountName[currentLanguage] ?? settings?.controls?.accountName['all'];
    }, [settings?.controls?.accountName]);

    const isShowingTitle = settings?.controls?.showTitleDuringPlayback
        ? settings?.controls?.showTitleDuringPlayback
        : !overShowingTime
            ? true
            : !isPlaying;
    const isShowingAccName = settings?.controls?.showAccountNameDuringPlayback
        ? settings?.controls?.showAccountNameDuringPlayback
        : !overShowingTime
            ? true
            : !isPlaying;
    const duration = currentMedia?.duration ?? durationReducer ?? 0;

    // Counting down, per the design - what is left of the clip, not how far in we are. It reads the
    // same total as the bar above it: on durationReducer alone the label kept the previous clip's
    // length for a beat after a slide change, while the bar had already reset. Ceil, not round, or
    // it reaches 00:00 with the bar still short of the end.
    const remainingLabel = useMemo(() => {
        const total = Number(duration) || 0;
        const left = Math.max(0, Math.ceil(total - (Number(currentPosition) || 0)));
        if (!total || isMediaLive) {
            return '';
        }
        const minutes = Math.floor(left / 60);
        const seconds = left % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }, [duration, currentPosition, isMediaLive]);

    const isInfoExcludedOnThisHost = (option: any, domainList: any) => {
        let isInfoHidden = false;

        const titleDomainList = (domainList ?? '').split(',');

        if (option && titleDomainList.length !== 0) {
            const url = embedShareSettings?.origin ?? '';
            isInfoHidden = titleDomainList.some((domain: any) => domain && url.includes(domain.trim()));
        }

        return isInfoHidden;
    };

    const mediaTitleHiddenOnThisHost = isInfoExcludedOnThisHost(
        settings?.controls?.showExcludingTitleOnDomains,
        settings?.controls?.excludingTitleOnDomains
    );

    const companyNameHiddenOnThisHost = isInfoExcludedOnThisHost(
        settings?.controls?.showExcludingAccountNameOnDomains,
        settings?.controls?.excludingAccountNameOnDomains
    );

    const getPercentageBaseOnCurrentPosition = useCallback(
        (unitPer = 100) => {
            if (typeof currentPosition === "string" || currentPosition < 0 || LiveHelper.isAtLive(isMediaLive, currentPosition, duration)) {
                return 100;
            }
            return (Math.abs(currentPosition) / Math.abs(duration)) * unitPer;
        },
        [currentPosition, duration, isMediaLive]
    );

    // Measured against the BAR, never the wrapper around it. The wrapper spans the whole card and
    // carries the left padding and the countdown, so seeking by its box landed every click short by
    // the padding at one end and by the countdown's width at the other - a couple of percent on the
    // left, nine on the right. The class test that used to pick between them looked for
    // `top-control--timeline`, which is not a class this component has ever rendered.
    const handleClickOnProgressBar = useCallback(
        (event: React.MouseEvent<HTMLElement> | MouseEvent) => {
            event.stopPropagation();
            const bar = timelineBarRef.current;
            const total = Number(duration) || 0;
            if (!bar || !total) {
                return;
            }
            const bcr = bar.getBoundingClientRect();
            // A click on the button's padding is a click on the nearest end, not a seek past it.
            const ratio = Math.min(1, Math.max(0, (event.clientX - bcr.left) / bcr.width));
            const xTiming = Math.round(ratio * total * 1000) / 1000;

            setPlayerCurrentPosition(xTiming);
            (window as any).GoBrain.widgets(currentPlayerId)?.position?.(xTiming);
            setIndicatorPosition(ratio * 100);
        },
        [currentPlayerId, duration, setPlayerCurrentPosition]
    );

    // Feeds only. The same component draws the bar for any Story player, and dragging is a change
    // of behaviour - the others keep tap-to-seek until someone asks otherwise.
    const isReelsFeed = isReelsFeedEnabled(configParams);
    const [isScrubbing, setIsScrubbing] = useState(false);

    // Dragging the bar, not just tapping it. The same maths as a click, repeated while the pointer
    // is down and captured so it keeps arriving once the finger leaves the 10px bar - which is most
    // of a drag. Pointer events rather than mouse ones, so a thumb and a mouse take the same path.
    const handleProgressPointerDown = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            const bar = timelineBarRef.current;
            if (!bar || !(Number(duration) || 0)) {
                return;
            }
            event.stopPropagation();
            const target = event.currentTarget;
            setIsScrubbing(true);
            target.setPointerCapture?.(event.pointerId);
            const scrub = (moveEvent: PointerEvent) => handleClickOnProgressBar(moveEvent as unknown as MouseEvent);
            const release = () => {
                setIsScrubbing(false);
                target.releasePointerCapture?.(event.pointerId);
                target.removeEventListener('pointermove', scrub);
                target.removeEventListener('pointerup', release);
                target.removeEventListener('pointercancel', release);
            };
            target.addEventListener('pointermove', scrub);
            target.addEventListener('pointerup', release);
            target.addEventListener('pointercancel', release);
            handleClickOnProgressBar(event.nativeEvent);
        },
        [duration, handleClickOnProgressBar]
    );

    useEffect(() => {
        setIndicatorPosition(getPercentageBaseOnCurrentPosition());
    }, [currentMedia?.duration, currentPosition, duration, durationReducer, getPercentageBaseOnCurrentPosition, setIndicatorPosition]);

    useEffect(() => {
        const clearTimeId = setTimeout(() => {
            setOverShowingTime(true);
        }, 3000);

        return () => {
            clearTimeout(clearTimeId);
        };
    }, []);

    return <TopControlsContainer
        story
        overlay={configParams?.inline === "off" || configParams.topRightSpace}
        className={joinClassNames(configParams?.inline === "off" ? 'top-control--story-overlay top-control--not-inline' : '', "top-control--story", className)}
    >
        <div className={joinClassNames(['top-control__timeline-wrapper', isScrubbing ? 'top-control__timeline-wrapper--scrubbing' : ''])}>
            {/* The hit area carries the drag: it is the padded box around a 10px bar, so a finger
                that starts a few pixels high still scrubs. swiper-no-swiping keeps the feed from
                reading the same gesture as a scroll. */}
            <div
                role='button'
                onClick={handleClickOnProgressBar}
                onPointerDown={isReelsFeed ? handleProgressPointerDown : undefined}
                className={joinClassNames(['top-control__timeline-button', isReelsFeed ? 'swiper-no-swiping' : ''])}
            >
                <div ref={timelineBarRef} className={"top-control__timeline"}>
                    <span
                        className="top-control__timeline--active"
                        style={isMediaLive && indicatorPosition === 0 ? { ...primaryColor } : { ...progressTranslate, ...primaryColor }}
                    />
                </div>
            </div>
            {remainingLabel && <span className="top-control__timeline-remaining">{remainingLabel}</span>}
        </div>

        <div className="top-control__title-wrapper">
            {settings?.controls.enabled &&
                settings?.controls?.showLogotype &&
                settings?.controls?.logotypeUrl &&
                !companyNameHiddenOnThisHost && (
                    <img
                        style={{
                            opacity: `${isShowingAccName ? 1 : 0}`,
                            transition: 'all 0.5s',
                        }}
                        aria-hidden={true}
                        src={settings?.controls?.logotypeUrl}
                    />
                )}
            <TopControlsPlaybackTitleWrapper>
                {settings?.controls.enabled && settings?.controls?.showTitle && !mediaTitleHiddenOnThisHost && (
                    <TopControlsPlaybackTitle
                        className="topControlbar__control--title"
                        aria-hidden={!isShowingTitle}
                        aria-label={`${t('ARIA_LABEL_MEDIA_TITLE')}${mediaName || currentMedia?.mediaName}`}
                        style={{
                            ...solidColorStyle,
                            width: 'fit-content',
                            borderRadius: '4px',
                            opacity: `${isShowingTitle ? 1 : 0}`,
                            transition: 'all 0.5s'
                        }}
                        role="heading"
                        aria-level={2}
                    >
                        <TopControlsMediaTitle aria-hidden="true" isMobile={isMobile}>
                            {mediaName || currentMedia?.mediaName}
                        </TopControlsMediaTitle>
                    </TopControlsPlaybackTitle>
                )}
                {settings?.controls.enabled && settings?.controls?.showAccountName && accountName && !companyNameHiddenOnThisHost && (
                    <TopControlsPlaybackTitle
                        className="topControlbar__control--accountName"
                        aria-label={`${t('ARIA_LABEL_PUBLISHER')}${accountName}`}
                        aria-hidden={!isShowingAccName}
                        style={{
                            ...solidColorStyle,
                            width: 'fit-content',
                            borderRadius: '4px',
                            opacity: `${isShowingAccName ? 1 : 0}`,
                            transition: 'all 0.5s'
                        }}
                        role="heading"
                        aria-level={2}
                    >
                        <TopControlsAccountTitle aria-hidden="true" isMobile={isMobile}>
                            {accountName}
                        </TopControlsAccountTitle>
                    </TopControlsPlaybackTitle>
                )}
            </TopControlsPlaybackTitleWrapper>
        </div>
    </TopControlsContainer>
}